import { CloseCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { Alert, Avatar, Col, Form, Modal, Radio, Row, Select, Skeleton, Upload } from 'antd';
import React, { useEffect, useRef, useState } from 'react';

import patterns from '../../config/patterns';
import './antd.form.css';
import './user-management.css';

import { countriesWithStates } from '../../constants/country-states';
import authService from '../../services/auth.service';
import { getBase64 } from '../../utils/files';
import { notifyError, notifySuccess } from '../../utils/notification';
import { phoneToObject } from '../../utils/phone-to-obj';
import { scrollUp } from '../../utils/scroll-to';
import { titlize } from '../../utils/strings';
import { TASelect } from '../dashboard/styles';
import { PhoneInput } from '../phone-input/phone-input';
import { ButtonType, TAButton, TAInput } from '../widgets';
import { transformToFormData } from '../../utils';
const { phoneNumberPattern } = patterns;

export const EditUserProfile: React.FC<any> = props => {
	const [loading, setLoading] = useState<boolean>(true);
	const [avatarPreviewVisible, setAvatarPreviewVisible] = useState<boolean>(false);
	const [avatarPreview, setAvatarPreview] = useState<any>('');
	const [selectedCountry, setSelectedCountry] = useState<any>('');
	const [errors, setErrors] = useState<any>({ phone: '' });
	const [error, setError] = useState<any>();
	const editUserRef = useRef<any>();
	const [form] = Form.useForm();

	const mountLogic = async () => {
		let user;
		try {
			user = await authService.getUserProfile();
			if (!user) throw new Error('Error while fetching data, Please try again later.');
		} catch (e: any) {
			console.error(e);
			notifyError(e.message);
			setLoading(false);
			setError(e.message);
			return;
		}
		setLoading(false);
		setAvatarPreview(user.avatar);
		setSelectedCountry(user.country);
		form.setFieldsValue({
			...user,
			phoneNumber: user.phoneNumber && phoneToObject(user.phoneNumber as string)
		});
	};

	useEffect(() => {
		mountLogic();
	}, []);

	const onFinish = async values => {
		if (avatarPreview) values.avatar = avatarPreview;
		if (values.phoneNumber?.phone) values.phoneNumber = `${values.phoneNumber.code}-${values.phoneNumber.phone}`;

		setLoading(true);
		let profile;
		const fd = transformToFormData(values);

		try {
			profile = await authService.updateProfile(fd);
		} catch (e: any) {
			scrollUp(300);
			setError(e.message);
			setLoading(false);
			notifyError(e.message);
			return;
		}
		notifySuccess('Profile updated successfully!');
		props.setUser && props.setUser(profile);
		setLoading(false);
	};

	const onAvatarUploadChange = info => {
		getBase64(info.file).then(imageUrl => setAvatarPreview(imageUrl));
	};

	const deleteAvatar = () => {
		setAvatarPreview(null);
	};

	const { user } = props;

	return (
		<div className={`ta-form md mx-auto my-3`}>
			<Row gutter={0} className="mb-4">
				<Col xs={{ span: 24 }} className="text-center text-md-start page-label">
					Edit your information
				</Col>
			</Row>
			{error && <Alert message="Error While fetching data" description={error} type="error" closable />}
			<Skeleton avatar active paragraph={{ rows: 15 }} loading={loading}>
				<div ref={editUserRef}>
					<Form layout="vertical" form={form} onFinish={onFinish} className="mb-4 box" scrollToFirstError>
						<Row gutter={8} className="mb-4">
							<Col
								xs={{ span: 24 }}
								md={{ span: 8 }}
								lg={{ span: 7 }}
								xl={{ span: 5 }}
								className="d-flex justify-content-center justify-content-md-start mb-3"
							>
								{avatarPreview ? (
									<div className="avatar-window" onClick={() => setAvatarPreviewVisible(true)}>
										<img className="photo" src={avatarPreview} />
									</div>
								) : user ? (
									<Avatar
										size={200}
										shape="square"
										style={{
											backgroundColor: user.avatarBgColor
										}}
									>
										<span
											style={{
												mixBlendMode: 'difference',
												width: '100%',
												fontSize: '5rem'
											}}
										>
											{`${titlize(user.firstName[0])}${titlize(user.lastName[0])}`}
										</span>
									</Avatar>
								) : null}
							</Col>

							<Col xs={{ span: 24 }} md={{ span: 15 }} className="text-center text-md-start">
								<p className="page-label text-center text-md-start">Edit your Photo</p>
								<p>You can upload a .jpg, .png, or .gif images with max size of 5MB.</p>
								<div className="d-flex align-items-stretch justify-content-center justify-content-md-start">
									<Form.Item name="avatar">
										<div className="d-flex">
											<Upload
												name="Avatar"
												beforeUpload={() => false}
												fileList={[]}
												onChange={onAvatarUploadChange}
											>
												<TAButton
													btntype={ButtonType.PRIMARY}
													className="d-flex align-items-center"
												>
													<UploadOutlined />
													<span>Upload</span>
												</TAButton>
											</Upload>

											<TAButton
												btntype={ButtonType.DANGER}
												onClick={deleteAvatar}
												className="d-flex align-items-center ms-2"
											>
												<CloseCircleOutlined />
												<span>Delete</span>
											</TAButton>
										</div>
									</Form.Item>
								</div>
								<Modal
									visible={avatarPreviewVisible}
									footer={null}
									onCancel={() => setAvatarPreviewVisible(false)}
								>
									<img alt="example" style={{ width: '100%' }} src={avatarPreview} />
								</Modal>
							</Col>
						</Row>
						<fieldset className="ta-form-section" id="account-section">
							<legend className="ta-form-section-title">Personal information</legend>
							<Row gutter={8}>
								<Col xs={12} md={24}>
									<Form.Item
										label="First Name"
										name="firstName"
										rules={[
											{ required: true, message: 'Please input your first name.' },
											{
												pattern: patterns.lettersOnlyPattern,
												message: 'Not valid name'
											}
										]}
										hasFeedback
									>
										<TAInput />
									</Form.Item>
								</Col>
								<Col xs={12} md={24}>
									<Form.Item
										label="Last Name"
										name="lastName"
										rules={[
											{ required: true, message: 'Please enter last name.' },
											{
												pattern: patterns.lettersOnlyPattern,
												message: 'Not valid name'
											}
										]}
									>
										<TAInput />
									</Form.Item>
								</Col>
							</Row>

							<Form.Item
								label="Title"
								name="title"
								rules={[{ required: true, message: 'Please enter your title.' }]}
							>
								<TAInput />
							</Form.Item>

							<Form.Item label="Gender" name="gender">
								<Radio.Group buttonStyle="solid">
									<Radio.Button value="male">Male</Radio.Button>
									<Radio.Button value="female">Female</Radio.Button>
								</Radio.Group>
							</Form.Item>

							<Form.Item
								label="Phone"
								name="phoneNumber"
								rules={[
									{ required: true },
									{
										validator: (rule, value, callback) => {
											if (!value || !value.code || !value.phone)
												callback('Must enter phone number and code');
											else if (!phoneNumberPattern.test(value.phone))
												callback('Not valid phone number');
											else callback();
										}
									}
								]}
							>
								<PhoneInput />
							</Form.Item>
						</fieldset>

						<fieldset className="ta-form-section" id="account-section">
							<legend className="ta-form-section-title">Location</legend>

							<Form.Item label="Country" name="country">
								<TASelect
									onChange={c => {
										form.setFieldsValue({ city: null });
										setSelectedCountry(c);
									}}
									showSearch
									filterOption={(input, option) =>
										option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
									}
								>
									{Object.keys(countriesWithStates).map(c => (
										<Select.Option value={c} key={c}>
											{c}
										</Select.Option>
									))}
								</TASelect>
							</Form.Item>

							<Form.Item label="City" name="city">
								<TASelect
									showSearch
									filterOption={(input, option) =>
										option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
									}
								>
									{selectedCountry &&
										countriesWithStates[selectedCountry].map(s => (
											<Select.Option value={s} key={s}>
												{s}
											</Select.Option>
										))}
								</TASelect>
							</Form.Item>
						</fieldset>

						<Form.Item>
							<TAButton
								btntype={ButtonType.PRIMARY}
								htmlType="submit"
								className="w-100 mt-4"
								style={{ height: '44px' }}
							>
								Save
							</TAButton>
						</Form.Item>
					</Form>
				</div>
			</Skeleton>
		</div>
	);
};
